/**
 * StatusPage — public health snapshot at `/status`.
 *
 * Polls `GET /api/status` every 30 s and renders one card per probed
 * service. Never behind auth; nurseries doing procurement look for
 * this URL as part of due diligence.
 *
 * Rendering rules:
 *   • overall = operational   → green banner + all-good tagline
 *   • overall = partial_outage → amber banner naming the affected svc
 *   • overall = major_outage  → red banner + incident guidance
 *   • fetch error itself      → grey banner ("cannot reach API")
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const POLL_MS = 30_000;
const HISTORY_DAYS = 30;

const STATUS_META = {
  up:       { color: '#0b7a5a', bg: '#e6f7f0', label: 'Operational', dot: '#0b7a5a' },
  degraded: { color: '#8a5a00', bg: '#fff5e0', label: 'Degraded',    dot: '#f4a800' },
  down:     { color: '#a12a2a', bg: '#fdeaea', label: 'Down',        dot: '#d43a3a' },
  unknown:  { color: '#4a5a68', bg: '#eef1f5', label: 'Unknown',     dot: '#8a95a3' },
};

const OVERALL_META = {
  operational:     { color: '#0b7a5a', bg: 'linear-gradient(120deg, #d8f0e6 0%, #eaf7f1 100%)', label: 'All systems operational', icon: '✅' },
  partial_outage:  { color: '#8a5a00', bg: 'linear-gradient(120deg, #fff2d5 0%, #fff9e8 100%)', label: 'Partial outage',           icon: '⚠️' },
  major_outage:    { color: '#a12a2a', bg: 'linear-gradient(120deg, #fce0e0 0%, #fdefef 100%)', label: 'Major outage',             icon: '⛔' },
  fetch_error:     { color: '#4a5a68', bg: 'linear-gradient(120deg, #e6ebf1 0%, #f2f5f9 100%)', label: 'Cannot reach status API',  icon: '❓' },
};

function timeAgo(iso) {
  if (!iso) return '—';
  const diff = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `${Math.round(diff)}s ago`;
  if (diff < 3600) return `${Math.round(diff/60)}m ago`;
  return `${Math.round(diff/3600)}h ago`;
}

/**
 * 30-day uptime strip — one small vertical bar per day, oldest → newest.
 *
 * Colour rules:
 *   • uptime_pct >= 99.9 → green
 *   • uptime_pct >= 95   → amber
 *   • uptime_pct <  95   → red
 *   • no samples that day → grey ("not monitored")
 *
 * A per-day tooltip surfaces the exact uptime % so procurement folks
 * doing DD can hover any tick without needing another page load.
 */
function UptimeStrip({ series, days = 30 }) {
  // Build a canonical, gap-filled series covering `days` days ending
  // today, so ticks always align vertically across services.
  const byDay = new Map((series || []).map((s) => [s.day, s]));
  const today = new Date();
  const canonical = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setUTCDate(today.getUTCDate() - i);
    const key = d.toISOString().slice(0, 10);
    canonical.push(byDay.get(key) || { day: key, uptime_pct: null, samples: 0 });
  }
  const rollingUptime = (() => {
    const seen = canonical.filter((c) => typeof c.uptime_pct === 'number');
    if (!seen.length) return null;
    const avg = seen.reduce((acc, c) => acc + c.uptime_pct, 0) / seen.length;
    return Math.round(avg * 100) / 100;
  })();

  const colorFor = (pct) => {
    if (pct === null || pct === undefined) return '#dfe6ec';
    if (pct >= 99.9) return '#22c07a';
    if (pct >= 95)  return '#f4a800';
    return '#d43a3a';
  };

  return (
    <div data-testid="uptime-strip" style={{ marginTop: '0.7rem' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 22 }}>
        {canonical.map((c) => (
          <div
            key={c.day}
            title={c.uptime_pct === null
              ? `${c.day} — not monitored`
              : `${c.day} — ${c.uptime_pct}% uptime (${c.samples} samples)`}
            style={{
              flex: 1, minWidth: 3, height: '100%',
              borderRadius: 2, background: colorFor(c.uptime_pct),
              transition: 'background 200ms',
            }}
          />
        ))}
      </div>
      <div style={{ marginTop: '0.35rem', display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: '#7a9794' }}>
        <span>{days} days ago</span>
        <span>
          {rollingUptime === null
            ? 'No history yet'
            : `${rollingUptime}% uptime`}
        </span>
        <span>Today</span>
      </div>
    </div>
  );
}

export default function StatusPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState(null);   // { days, services: {name: [{day, uptime_pct, samples}]} }

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/status`, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to fetch status');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const h = await api.getStatusHistory(HISTORY_DAYS);
      setHistory(h);
    } catch {
      // Non-critical — the live tiles still render.
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    fetchHistory();
    const id = setInterval(() => { fetchStatus(); fetchHistory(); }, POLL_MS);
    return () => clearInterval(id);
  }, [fetchStatus, fetchHistory]);

  const overall = useMemo(() => {
    if (error) return OVERALL_META.fetch_error;
    if (!data) return OVERALL_META.operational;
    return OVERALL_META[data.overall] || OVERALL_META.fetch_error;
  }, [data, error]);

  return (
    <div data-testid="status-page-root" style={pageStyle}>
      <header style={headerStyle}>
        <Link to="/" data-testid="status-home-link" style={{ color: '#0b3d3a', fontWeight: 700, textDecoration: 'none' }}>
          ← Kids WiseApp
        </Link>
        <div style={{ fontSize: '0.8rem', color: '#7a9794' }}>
          Public status
        </div>
      </header>

      <main style={mainStyle}>
        <h1 style={h1Style}>System status</h1>
        <p style={leadStyle}>
          Live health of every Kids WiseApp service. Refreshes every 30 seconds. Bookmark this page — no
          login required.
        </p>

        {/* Overall banner */}
        <div
          data-testid="status-overall-banner"
          style={{
            ...overallBannerStyle,
            background: overall.bg,
            borderColor: overall.color,
            color: overall.color,
          }}
        >
          <div style={{ fontSize: '1.8rem' }}>{overall.icon}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '1.15rem', fontWeight: 700 }}>{overall.label}</div>
            {data && (
              <div style={{ fontSize: '0.82rem', opacity: 0.85, marginTop: 2 }}>
                Last checked {timeAgo(data.checked_at)}
              </div>
            )}
            {error && (
              <div style={{ fontSize: '0.82rem', opacity: 0.85, marginTop: 2 }}>
                {error} — retrying in {Math.round(POLL_MS/1000)}s
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={fetchStatus}
            data-testid="status-refresh-button"
            style={{
              border: `1px solid ${overall.color}`,
              background: 'rgba(255,255,255,0.6)',
              color: overall.color,
              padding: '0.4rem 0.85rem',
              borderRadius: 999,
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.82rem',
            }}
          >
            🔄 Refresh
          </button>
        </div>

        {/* Service tiles */}
        <div style={gridStyle}>
          {loading && !data && (
            <div data-testid="status-loading" style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#7a9794', padding: '2rem' }}>
              Loading…
            </div>
          )}
          {data && (data.services || []).map((svc) => {
            const meta = STATUS_META[svc.status] || STATUS_META.unknown;
            return (
              <div
                key={svc.name}
                data-testid={`status-service-${(svc.name || '').replace(/\s+/g, '-').toLowerCase()}`}
                style={{
                  ...cardStyle,
                  borderColor: meta.color,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span
                    aria-hidden
                    style={{
                      width: 10, height: 10, borderRadius: '50%',
                      background: meta.dot,
                      boxShadow: `0 0 0 3px ${meta.bg}`,
                    }}
                  />
                  <div style={{ fontWeight: 700, fontSize: '1rem', color: '#0b3d3a' }}>{svc.name}</div>
                </div>
                <div style={{
                  marginTop: '0.5rem',
                  display: 'inline-block',
                  padding: '0.15rem 0.6rem',
                  borderRadius: 999,
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  color: meta.color,
                  background: meta.bg,
                  letterSpacing: '0.03em',
                  textTransform: 'uppercase',
                }}>
                  {meta.label}
                </div>
                <div style={{ marginTop: '0.5rem', fontSize: '0.78rem', color: '#5a6b7a' }}>
                  {typeof svc.latency_ms === 'number' ? `${svc.latency_ms} ms` : '—'}
                </div>
                {svc.detail && (
                  <div style={{ marginTop: '0.35rem', fontSize: '0.75rem', color: meta.color, lineHeight: 1.4 }}>
                    {svc.detail}
                  </div>
                )}
                {/* 30-day uptime strip — 1 tick per day, coloured by
                    that day's uptime %. Days with no samples render
                    as flat grey so we don't imply 100% for a day
                    where the service wasn't even monitored. */}
                <UptimeStrip series={(history && history.services && history.services[svc.name]) || []} days={HISTORY_DAYS} />
              </div>
            );
          })}
        </div>

        <footer style={footerStyle}>
          <div>
            Something looking wrong? Email{' '}
            <a href="mailto:hello@kidswiseapp.com" style={{ color: '#0b3d3a', fontWeight: 600 }}>
              hello@kidswiseapp.com
            </a>{' '}— we usually reply within an hour during working days.
          </div>
          <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#7a9794' }}>
            Auto-refreshes every {Math.round(POLL_MS/1000)} seconds · Powered by Kids WiseApp
          </div>
        </footer>
      </main>
    </div>
  );
}

// ---------- Styles ----------
const pageStyle = {
  minHeight: '100vh',
  background: '#f6fbfa',
  color: '#0b3d3a',
  fontFamily: 'inherit',
};

const headerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '1rem 1.5rem',
  borderBottom: '1px solid #dbeae7',
  background: 'white',
};

const mainStyle = {
  maxWidth: 920,
  margin: '0 auto',
  padding: '2.5rem 1.5rem 3rem',
};

const h1Style = {
  margin: 0,
  fontSize: '2rem',
  fontWeight: 800,
  letterSpacing: '-0.01em',
};

const leadStyle = {
  margin: '0.5rem 0 1.5rem',
  color: '#5a7371',
  fontSize: '0.95rem',
  lineHeight: 1.55,
};

const overallBannerStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '1rem',
  padding: '1rem 1.25rem',
  border: '1px solid',
  borderRadius: 14,
  marginBottom: '1.5rem',
};

const gridStyle = {
  display: 'grid',
  gap: '0.85rem',
  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
};

const cardStyle = {
  background: 'white',
  border: '1px solid',
  borderColor: '#dbeae7',
  borderRadius: 12,
  padding: '1rem',
  boxShadow: '0 1px 2px rgba(11, 61, 58, 0.05)',
};

const footerStyle = {
  marginTop: '2rem',
  paddingTop: '1.5rem',
  borderTop: '1px solid #dbeae7',
  fontSize: '0.85rem',
  color: '#5a7371',
};
